import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDriversList, assignDriverManually, setClubbingAssignmentLoading, releasePrimaryDriver } from "../redux/expressSlice";
import { ClubbingSuggestions } from  "../../utils/utiles.types"; // Adjust the path to where ClubbingSuggestion is defined
import { AppState } from "../../config/redux/reducers";
import { Grid, Box, GridSize, List} from "@material-ui/core";
import { useStyles } from "./DriverAssignment.styles";
import AppConstants from "../../constants";
import { TextConstants } from "constants/TextConstants";
import { createPayloadToFetchDriverList, createPayloadToAssignDriver } from "../../mocks/express/response.transforms";
import { DriversFooter, DriversHeader, DriversList, DriversSearchHeader } from "./DriverCommonComponent";
import { useDriverVehicleFilter } from "./useDriverVehicleFilter";
import { useDriverSearch } from "./useDriverSearch";
import { hasValidClubbingSuggestions } from "utils/productUtils";
import { assignClubbedOrders, trackAssignAction } from "./driverAssignment.service";
import { useError } from "contexts/ErrorContext";

interface DriverAssignmentProps {
  consignment?: any;
  countryCode?: string;
  clubbingSuggestions?: ClubbingSuggestions;
  selectedConsignmentCodes?: string[];
  isClubbingEnabled?: boolean;
  isOrderRestricted?: boolean;
  rtdBreached?: boolean;
}

const PrimaryDriver = (props: DriverAssignmentProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [selectedDrivers, setSelectedDrivers] = useState<any[]>([]);
  const [isDriverSelected, setIsDriverSelected] = useState(false);
  const { consignment, countryCode, clubbingSuggestions, selectedConsignmentCodes = [], isClubbingEnabled, isOrderRestricted, rtdBreached } = props;
  const { driversList } = useSelector((state: AppState) => state.expressDashboard);
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const { showError } = useError();

  const vehicleFilter = useDriverVehicleFilter(driversList);
  const driverSearch = useDriverSearch({
    driversList,
    filterByVehicle: vehicleFilter.filterByVehicle,
  });

  useEffect(() => {
    driverSearch.handleSearch(driverSearch.searchValue);
  }, [vehicleFilter.selectedVehicle, driversList,  driverSearch.handleSearch, driverSearch.searchValue]);

  useEffect(() => {
    setSelectedDrivers([]);
  }, [driversList]);

  const isDriverSelectable = (driver: any): boolean => {
    const driverStatus = driver?.status?.toLowerCase();
    const driverCategory = driver?.category?.toLowerCase();
    return !(
      driverStatus === AppConstants.EXPRESS_CONSTANTS.DRIVER_STATUS.OUT_FOR_DELIVERY.toLowerCase() || driverStatus === AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ORDER_STATUS.OUT_FOR_DELIVERY.toLowerCase() ||
      driverCategory === AppConstants.EXPRESS_CONSTANTS.DRIVERS_CATEGORY.OTHERS.toLowerCase()
    );
  };

  const handleListItemClick = useCallback((event: any, driver: any) => {
    setSelectedDrivers([driver]);
    setIsDriverSelected(isDriverSelectable(driver));
  }, []);

  const manuallyAssignDriver = useCallback(
    (selectedDriver: any, consignment: any) => {
      const payload = createPayloadToAssignDriver(selectedDriver, consignment);
      dispatch(assignDriverManually({ payload }));
    },
    [dispatch]
  );

  const handleAssignDriver = useCallback(() => {
    if (selectedDrivers?.length > 0) {
      const selectedIDs = selectedDrivers.map((driver) => driver.driverId);
      if (selectedIDs.length > 0) {
        manuallyAssignDriver(selectedIDs, consignment);
        if (isClubbingEnabled) {
          const ignoredIds = hasValidClubbingSuggestions(clubbingSuggestions)
            ? clubbingSuggestions?.orders?.map(
                (order: any) => order.consignmentCode
              ) || []
            : [];
          const readyState = !isOrderRestricted
          trackAssignAction({
            actionType: "Assign",
            ignoredIds,
            clubbedIds: [],
            clubbedCount: 0,
            storeId: consignment?.posInformation?.store || "",
            selectedConsignmentId: consignment?.consignmentCode,
            readyState,
            timerPassed: rtdBreached,
            activityType: consignment?.metadata?.activity,
            driverId: selectedDrivers[0]?.driverId,
            assignments: selectedDrivers[0]?.assignments,
          });
        }
      }
    }
  }, [selectedDrivers, consignment, manuallyAssignDriver, clubbingSuggestions, isClubbingEnabled]);

  const handleCancel = useCallback(() => {
    driverSearch.resetSearch();
    setIsDriverSelected(false);
    setSelectedDrivers([]);
    vehicleFilter.resetVehicleFilter();
  }, [driverSearch, vehicleFilter]);

  const fetchDriversLists = useCallback(
    (consignment: any, countryCode: any) => {
      if (Object.keys(consignment).length) {
        const payload = createPayloadToFetchDriverList(countryCode, consignment.posInformation, consignment.metadata?.activity);
        dispatch(fetchDriversList({ payload }));
        setSelectedDrivers([]);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (consignment !== null) {
      fetchDriversLists(consignment, countryCode);
      setIsDriverSelected(false);
      vehicleFilter.resetVehicleFilter();
    }
  }, [fetchDriversLists, countryCode, consignment, vehicleFilter.resetVehicleFilter]);

  const headerColumns = useMemo(
    () => [
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.DRIVER_NAME, xs: 4 as GridSize },
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.VEHICLE, xs: 2 as GridSize, style: { textAlign: "center" as const } },
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.STATUS, xs: 3 as GridSize, style: { textAlign: "center" as const } },
      {
        label: (
          <span>
            {AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.PROXIMITY}
            <br />
            {AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.TO_HUB}
          </span>
        ),
        xs: 2 as GridSize,
        style: { textAlign: "center" as const },
      },
      { label: AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST_HEADER.ORDERS, xs: 1 as GridSize, style: { textAlign: "right" as const } },
    ],
    []
  );

  const handleAssignClubbed = useCallback(async () => {
    if (selectedDrivers?.length === 0 || selectedConsignmentCodes.length === 0) {
      return;
    }

    dispatch(setClubbingAssignmentLoading(true));
    
    try {
      await assignClubbedOrders({
        clubbingSuggestions,
        selectedDriverId: selectedDrivers[0].driverId,
        consignment,
        selectedConsignmentCodes,
        storeId: consignment?.posInformation?.store || "",
        rtdBreached: rtdBreached,
        assignments: selectedDrivers[0]?.assignments,
        assignDriverManually: async (payload: any) => {
          await dispatch(assignDriverManually({ payload, skipLoading: true }));
        },
        removePrimaryDriver: async (payload: any) => {
          await dispatch(releasePrimaryDriver({ payload }));
        },
      });
    } catch(error: any){
      showError(error.message || TextConstants.UNEXPECTED_ERROR);
    } finally {
      dispatch(setClubbingAssignmentLoading(false));
    }
  }, [selectedDrivers, consignment, clubbingSuggestions, selectedConsignmentCodes, dispatch]);

  const isAssignButtonDisabled = useCallback(() => {
    return !isDriverSelected || (hasValidClubbingSuggestions(clubbingSuggestions, rtdBreached) && (clubbingSuggestions?.orders?.length || 0) === 1);
  }, [isDriverSelected, clubbingSuggestions, rtdBreached]);

  return (
    <>
      <DriversSearchHeader
        consignment={consignment}
        handleSearchBoxTextChange={driverSearch.handleSearchBoxTextChange}
        searchValue={driverSearch.searchValue}
        searchBoxRef={searchBoxRef}
        classes={classes}
        fetchDriversLists={fetchDriversLists}
        countryCode={countryCode}
        title={AppConstants.EXPRESS_CONSTANTS.TEXT_CONSTANTS.DRIVER_LIST}
        {...vehicleFilter}
      />
      <Grid className="driverListContainer" container direction="column">
        <DriversHeader columns={headerColumns} className="itemContainer header" />
        <Box className={classes.driverList}>
          <List component="nav" aria-label="driversList" style={{ paddingTop: "0px" }}>
            <DriversList
              listToShow={driverSearch.listToShow}
              selectedDrivers={selectedDrivers}
              handleListItemClick={handleListItemClick}
              classes={classes}
              isPrimary
            />
          </List>
        </Box>
        <DriversFooter
          isDriverSelected={isDriverSelected}
          handleAssignDriver={handleAssignDriver}
          handleCancel={handleCancel}
          handleAssignClubbed={handleAssignClubbed}
          hasClubbingSuggestions={hasValidClubbingSuggestions(clubbingSuggestions, rtdBreached)}
          clubbingSuggestionLength={clubbingSuggestions?.orders?.length || 0}
          isAssignDisabled={isAssignButtonDisabled()}
        />
      </Grid>
    </>
  );
};

export default React.memo(PrimaryDriver);